import React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';

export type AccuracyLevel = 'perfect' | 'good' | 'acceptable' | 'poor' | 'very-poor';

interface AccuracyIndicatorProps {
  accuracy: AccuracyLevel;
  distance?: number;
  provinceName?: string;
  isVisible?: boolean;
  position?: { x: number; y: number } | null;
}

const ACCURACY_CONFIG = {
  perfect: {
    color: 'bg-green-500',
    textColor: 'text-white',
    text: '完美！',
    icon: '🎯',
    description: '精确命中目标区域'
  },
  good: {
    color: 'bg-blue-500',
    textColor: 'text-white', 
    text: '很好！',
    icon: '✅',
    description: '非常接近正确位置'
  },
  acceptable: {
    color: 'bg-yellow-500',
    textColor: 'text-white',
    text: '可以',
    icon: '👍',
    description: '在可接受范围内'
  },
  poor: {
    color: 'bg-orange-500',
    textColor: 'text-white',
    text: '太远了',
    icon: '📍',
    description: '距离目标较远'
  },
  'very-poor': {
    color: 'bg-red-500',
    textColor: 'text-white',
    text: '错误位置',
    icon: '❌',
    description: '完全错误的位置'
  }
};

export const AccuracyIndicator: React.FC<AccuracyIndicatorProps> = ({
  accuracy,
  distance,
  provinceName,
  isVisible = true,
  position
}) => {
  const config = ACCURACY_CONFIG[accuracy];

  if (!isVisible) return null;

  return (
    <motion.div
      className={clsx(
        'absolute z-50 px-3 py-2 rounded-lg shadow-lg pointer-events-none',
        'flex items-center space-x-2 text-sm font-medium',
        config.color,
        config.textColor
      )}
      style={{
        left: position?.x || 0,
        top: position?.y || 0,
        transform: 'translate(-50%, -120%)'
      }}
      initial={{ opacity: 0, scale: 0.8, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.8, y: -10 }}
      transition={{ 
        type: 'spring',
        stiffness: 400,
        damping: 25
      }}
    >
      {/* 图标 */}
      <span className="text-lg">{config.icon}</span>
      
      {/* 主要文本 */}
      <span>{config.text}</span>
      
      {/* 距离信息 */}
      {distance !== undefined && distance > 0 && (
        <span className="text-xs opacity-90">
          ({Math.round(distance * 100)}km)
        </span>
      )}
      
      {/* 省份名称 */}
      {provinceName && (
        <span className="text-xs opacity-90 max-w-20 truncate">
          {provinceName}
        </span>
      )}
      
      {/* 小箭头 */}
      <div 
        className={clsx(
          'absolute top-full left-1/2 transform -translate-x-1/2',
          'w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent',
          config.color.replace('bg-', 'border-t-')
        )}
      />
    </motion.div>
  );
};

/**
 * 根据距离和容错范围计算精度等级
 */
export function calculateAccuracy(
  distance: number, 
  tolerance: number,
  isInExactArea: boolean = false
): AccuracyLevel {
  if (isInExactArea) {
    return 'perfect';
  }
  
  if (distance <= tolerance * 0.3) {
    return 'good';
  } else if (distance <= tolerance * 0.7) {
    return 'acceptable';
  } else if (distance <= tolerance) {
    return 'poor';
  } else {
    return 'very-poor';
  }
}

/**
 * 获取精度等级的描述信息
 */
export function getAccuracyDescription(accuracy: AccuracyLevel): string {
  return ACCURACY_CONFIG[accuracy].description;
}

/**
 * 精度等级统计组件
 */
interface AccuracyStatsProps {
  stats: Record<AccuracyLevel, number>;
  total: number;
}

export const AccuracyStats: React.FC<AccuracyStatsProps> = ({ stats, total }) => {
  return (
    <div className="bg-white rounded-lg shadow-sm border p-4">
      <h4 className="font-semibold text-gray-800 mb-3">🎯 放置精度统计</h4>
      <div className="space-y-2">
        {Object.entries(stats).map(([accuracy, count]) => {
          const config = ACCURACY_CONFIG[accuracy as AccuracyLevel];
          const percentage = total > 0 ? (count / total * 100).toFixed(1) : '0';
          
          return (
            <div key={accuracy} className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span>{config.icon}</span>
                <span className="text-sm text-gray-700">{config.text}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium">{count}</span>
                <span className="text-xs text-gray-500">({percentage}%)</span>
              </div>
            </div>
          );
        })}
      </div>
      
      {total > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            总计: {total} 次放置
          </div>
        </div>
      )}
    </div>
  );
};
